import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Slot } from "@radix-ui/react-slot";
import css from "./ui.module.css";
import { cx } from "./cx.ts";
import { Icon, type IconSize } from "./Icon.tsx";
import { Tooltip } from "./Tooltip.tsx";

type Kind = "primary" | "secondary" | "ghost" | "danger" | "voice";
type Size = "sm" | "md" | "lg";

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: Kind;
  size?: Size;
  busy?: boolean;
  full?: boolean;
  icon?: LucideIcon;
  asChild?: boolean;
}

/** Düğme (§6.1). `busy` → genişlik sabit, dönen simge yok, `aria-busy`. */
export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { asChild = false, kind = "secondary", size = "md", busy, full, icon, className, children, disabled, type = "button", ...rest },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      className={cx(css.btn, css[kind], size !== "md" && css[`btn_${size}`], full && css.full, className)}
      disabled={disabled}
      aria-busy={busy || undefined}
      {...rest}
      onClick={busy ? undefined : rest.onClick}
    >
      {asChild ? children : (
        <>
          {icon && <Icon icon={icon} size={size === "lg" ? "md" : "sm"} />}
          {children}
        </>
      )}
    </Comp>
  );
});

export interface IconBtnProps extends Omit<BtnProps, "children" | "icon"> {
  icon: LucideIcon;
  /** Zorunlu: erişilebilir ad + ipucu. */
  label: string;
  iconSize?: IconSize;
  round?: boolean;
  danger?: boolean;
  tip?: boolean;
  children?: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconBtnProps>(function IconButton(
  { asChild = false, icon, label, kind = "ghost", size = "md", iconSize = "md", round, danger, tip = true, className, children, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  const btn = (
    <Comp
      ref={ref}
      type={asChild ? undefined : "button"}
      aria-label={label}
      className={cx(css.btn, css[kind], css.iconBtn, size !== "md" && css[`btn_${size}`], round && css.round, danger && css.iconDanger, className)}
      {...rest}
    >
      {asChild ? children : <Icon icon={icon} size={iconSize} />}
    </Comp>
  );
  return tip ? <Tooltip label={label}>{btn}</Tooltip> : btn;
});
